import {
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  useToast,
  Switch,
  Icon,
  Button,
  Divider,
  IconButton,
  useClipboard,
  Box,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import actions from "../actions";
import {
  KYDOrganizationFull,
  KYDOrganizationSettings,
} from "@common/interfaces/KYDOrganization";
import { useDialog } from "../Common/Dialog";
import { FaCheck, FaWindowClose } from "react-icons/fa";
import { showSuccessToast } from "../utils";
import { FiCopy } from "react-icons/fi";
import { loadFbSdk } from "./EventDetail/Nav/Marketing/util";
import MetaAdsSetting from "./MetaAdsSetting";

const Settings = () => {
  const [organization, setOrganization] = useState<KYDOrganizationFull | null>(
    null
  );
  const { onCopy, hasCopied } = useClipboard(organization?.id);

  const [loading, setLoading] = useState<any>({});

  const toast = useToast();
  const dialog = useDialog();

  const updateLoading = useCallback((key: string, value: boolean) => {
    setLoading((l: any) => {
      return { ...l, [key]: value };
    });
  }, []);

  const onLinkStripeAccount = useCallback(() => {
    window.location.href = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${organization.stripe_oauth_client_id}&scope=read_write`;
  }, [organization]);

  const onStripeRedirect = useCallback(async (code: string) => {
    updateLoading("stripe", true);
    try {
      await actions.ADMIN.updateStripeConnection({ code });
      await fetchSettings(true);
    } catch (err) {
      dialog({ text: err.message });
    }
    updateLoading("stripe", false);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code && organization && !organization.stripe_connect_id) {
      onStripeRedirect(code);
    }
  }, [organization]);

  const fetchSettings = useCallback(
    async (silent?: boolean) => {
      if (!silent) {
        updateLoading("init", true);
      }
      let org;
      try {
        const _ = await actions.ADMIN.fetchOrganization();
        if (_) {
          setOrganization(_);
          org = _;
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
      return org;
    },
    [updateLoading, dialog]
  );

  useEffect(() => {
    fetchSettings();
  }, [updateLoading, fetchSettings]);

  const onUpdateSettings = useCallback(
    async (settings: Partial<KYDOrganizationSettings>) => {
      updateLoading("organization", true);
      try {
        await actions.ADMIN.updateOrganization("settings", settings);
        await fetchSettings(true);
        toast({
          title: "Settings Updated",
          description: `Your settings have been updated`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("organization", false);
    },
    [fetchSettings, updateLoading, dialog]
  );

  console.log({
    organization,
  });
  const sdkInit = useCallback(() => {
    //@ts-ignore
    if ("FB" in window && organization) {
      const APP_ID = organization.ads_manager?.app_id;
      //@ts-ignore
      window.FB.init({
        appId: APP_ID,
        xfbml: true,
        version: "v21.0",
      });

      console.log("INIT called with APP_ID", APP_ID);
    }
  }, [organization]);

  const triggerFacebookLogin = useCallback(() => {
    updateLoading("meta", true);
    //@ts-ignore
    window.FB.login(
      (response: {
        authResponse: {
          code: string;
        };
      }) => {
        if (!response.authResponse.code) {
          toast({
            title: "Login cancelled",
            description: `You cancelled the facebook login`,
            status: "error",
          });
          updateLoading("meta", false);
          return;
        }
        return actions.ADMIN.createOrganizationAccess({
          code: response.authResponse.code,
        })
          .then(() => {
            toast({
              status: "success",
              title: "Access Granted",
              description:
                "You've successfully connected your organization to Meta ads via KYD dashboard",
            });
            return fetchSettings(true);
          })
          .catch((err) => {
            toast({
              status: "error",
              title: "Error",
              description: `Error enabling KYD access to Meta ads`,
            });
          })
          .finally(() => {
            updateLoading("meta", false);
          });
      },
      {
        config_id: organization?.ads_manager?.config_id,
        response_type: "code",
        override_default_response_type: true,
      }
    );
  }, [organization]);

  const removeOrganizationAccess = useCallback(() => {
    updateLoading("meta", true);
    actions.ADMIN.removeOrganizationAccess().then(() => {
      toast({
        status: "success",
        title: "Access removed",
        description:
          "KYD access to Meta ads has been removed. You can no longer manage campaigns via KYD dashboard",
      });
      fetchSettings(true).then(() => {
        updateLoading("meta", false);
      });
    });
  }, [fetchSettings]);

  useEffect(() => {
    if (organization) {
      //@ts-ignore
      window.fbAsyncInit = sdkInit;
      loadFbSdk();
    }
  }, [sdkInit, organization]);

  return (
    <VStack w="100%">
      <Stack w="100%" maxW={"500px"} spacing={{ base: "8", lg: "6" }}>
        <Stack
          spacing="4"
          direction={{ base: "column", lg: "row" }}
          justify="space-between"
          align={{ base: "start", lg: "center" }}
        >
          <Stack spacing="1">
            <Heading
              size={useBreakpointValue({ base: "xs", lg: "sm" })}
              fontWeight="medium"
            >
              <HStack>
                <Text fontSize={"lg"} fontWeight="bold">
                  Settings
                </Text>
              </HStack>
            </Heading>
          </Stack>
        </Stack>

        {loading["init"] && (
          <VStack>
            <Spinner />
            <Text>Loading settings...</Text>
          </VStack>
        )}

        {organization && (
          <Stack spacing={{ base: "5", lg: "6" }}>
            <Stack>
              <Text fontWeight={"medium"}>Reminders</Text>
              <HStack
                justify={"space-between"}
                p={2}
                borderWidth={"1px"}
                rounded={"md"}
                borderColor={"gray.200"}
              >
                <Text>24 Hour Reminder Texts</Text>
                <Switch
                  defaultChecked={
                    !organization.settings.supress_first_reminder_sms
                  }
                  isDisabled={loading.organization}
                  colorScheme={"green"}
                  onChange={(e) =>
                    onUpdateSettings({
                      supress_first_reminder_sms: !e.target.checked,
                      supress_second_reminder_sms:
                        !!organization.settings.supress_second_reminder_sms,
                    })
                  }
                />
              </HStack>
              <HStack
                justify={"space-between"}
                p={2}
                borderWidth={"1px"}
                rounded={"md"}
                borderColor={"gray.200"}
              >
                <Text>1 Hour Reminder Texts</Text>
                <Switch
                  defaultChecked={
                    !organization.settings.supress_second_reminder_sms
                  }
                  isDisabled={loading.organization}
                  colorScheme={"green"}
                  onChange={(e) =>
                    onUpdateSettings({
                      supress_first_reminder_sms:
                        !!organization.settings.supress_first_reminder_sms,
                      supress_second_reminder_sms: !e.target.checked,
                    })
                  }
                />
              </HStack>
            </Stack>
            <Divider />
            <Stack>
              <Text fontWeight={"medium"}>Payouts</Text>
              {organization.stripe_connect_id ? (
                <HStack>
                  <Icon as={FaCheck} color="green" />
                  <Text>
                    Stripe Account Linked: {organization.stripe_connect_id}
                  </Text>
                </HStack>
              ) : (
                <Stack>
                  {loading["stripe"] ? (
                    <HStack>
                      <Text>Linking stripe account...</Text>
                    </HStack>
                  ) : (
                    <HStack>
                      <Icon as={FaWindowClose} color="red" />
                      <Text>Stripe Account Not Linked</Text>
                    </HStack>
                  )}
                  <Button
                    isLoading={loading["stripe"]}
                    isDisabled={loading["stripe"] || !organization}
                    onClick={onLinkStripeAccount}
                  >
                    Link Stripe Account
                  </Button>
                </Stack>
              )}

              <MetaAdsSetting
                show={!!organization.ads_manager?.config_id}
                organization={organization}
                onConnect={triggerFacebookLogin}
                onDisconnect={removeOrganizationAccess}
                isLoading={loading["meta"]}
              />

              <Stack spacing={4} mt={8} w="full">
                <VStack w="full">
                  <Text w="full" fontWeight={"medium"}>
                    Share your organization
                  </Text>
                  <Text fontSize={"xs"}>
                    Use the below id to share with another KYD user, allowing
                    them to share events with you
                  </Text>
                </VStack>
                <HStack w="full">
                  <Box flexGrow={1}>
                    <Text>Organization ID:</Text>
                    <Text>{organization?.id}</Text>
                  </Box>

                  <IconButton
                    variant={"outline"}
                    onClick={() => {
                      onCopy();
                      showSuccessToast(toast, "Organization ID copied");
                    }}
                    icon={<FiCopy />}
                    aria-label="Copy organization id"
                  />
                </HStack>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </VStack>
  );
};

export default Settings;
